import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';

import { Modal } from 'components/Modal';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { useAuth } from 'lib/auth';
import { paths } from 'routes';
import { usesNutsLocations } from 'utils/jobAdLocations';

import { AdditionalDetails } from './AdditionalDetails';
import { UnvalidatedSupplierWarning } from './UnvalidatedSupplierWarning';
import { useCreate } from '../api/create';
import { mainInfoSchema } from '../validation';
import { jobAdToRepostValues } from '../utils/jobAdToRepostValues';

const REPOSTABLE_OWNER_TYPES = ['Framework', 'DPS'];

export const RepostButton = ({ jobAd, disabled }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const company = user?.Company;
  const [openForm, setOpenForm] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const usesNuts = usesNutsLocations(company);
  const methods = useForm({
    defaultValues: jobAdToRepostValues(jobAd, usesNuts),
    resolver: useYupValidationResolver(mainInfoSchema(1)),
  });
  const { mutate, isLoading } = useCreate({ successMessage: 'Job Advertisement re-posted successfully' });

  useEffect(() => {
    if (openForm) {
      methods.reset(jobAdToRepostValues(jobAd, usesNuts));
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openForm, jobAd, usesNuts, methods]);

  const isOwner = !!company?.company_id && (company.company_id === jobAd?.supplier_id || company.company_id === jobAd?.buyer_id);
  const canRepost =
    company?.Type === 'supplier' &&
    REPOSTABLE_OWNER_TYPES.includes(jobAd?.owner_type) &&
    jobAd?.can_offer !== false &&
    !isOwner;

  if (!canRepost) return null;

  if (jobAd.reposted_ad_id) {
    return (
      <Button
        size="medium"
        variant="outlined"
        color="primary"
        sx={{ mb: 0.5, width: 1 }}
        onClick={() => navigate(paths.myJobAdsDetailsView(jobAd.reposted_ad_id))}
      >
        View my re-post
      </Button>
    );
  }

  const handleOpen = () => {
    if (!company?.validated) {
      setShowWarning(true);
    } else {
      setOpenForm(true);
    }
  };

  const onSubmit = (values) => {
    if (isLoading) return;
    mutate(values, {
      onSuccess: (response) => {
        setOpenForm(false);
        navigate(paths.myJobAdsDetailsView(response?.data?.ad_id));
      },
    });
  };

  return (
    <>
      <Button size="medium" variant="outlined" color="primary" sx={{ mb: 0.5, width: 1 }} onClick={handleOpen} disabled={disabled}>
        Re-post as my ad
      </Button>
      <Modal open={showWarning} handleClose={() => setShowWarning(false)} title="Unvalidated Supplier">
        <UnvalidatedSupplierWarning companyName={company?.Name} />
      </Modal>
      {openForm && (
        <AdditionalDetails
          title="Re-posting Advertisement"
          companyID={company?.company_id}
          buyerFlow={false}
          methods={methods}
          onSubmit={onSubmit}
          handleCloseModal={() => setOpenForm(false)}
        />
      )}
    </>
  );
};
